import React, { useRef, useState, useEffect } from 'react';
import { useReactive } from 'ahooks';
import { message } from 'kenshin';
import { PlusOutlined } from '@ant-design/icons';
import ConversationList from './components/ConversationList/index';
import ChatArea from './components/ChatArea';
import AssetLibrary from './components/AssetLibrary';
import { getAiSessionDetail } from '@/_serveice/aiCreation';
import './index.less';

// 三种创作模式
const MODES = {
  CHAT: 'chat',
  IMAGE: 'image',
  VIDEO: 'video',
};

const AiCreationCenter = () => {
  const state = useReactive({
    mode: MODES.CHAT, // 当前模式，默认聊天
    sessionId: null, // 当前会话ID (后端 sessionId)
    assetLibraryVisible: false, // 资产库是否展开
    conversationLoading: false, // 切换会话 loading
    sidebarCollapsed: false, // 侧边栏是否折叠
  });

  const chatAreaRef = useRef();
  const conversationListRef = useRef();
  const [dragOver, setDragOver] = useState(false);

  // 拖拽事件 - 监听整个主区域
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // 只有离开 main 区域时才关闭遮罩
    const mainEl = e.currentTarget;
    if (!mainEl.contains(e.relatedTarget)) {
      setDragOver(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith('image/'),
    );
    if (files.length === 0) {
      message.warning('仅支持拖入图片文件');
      return;
    }
    chatAreaRef.current?.addDroppedFiles(files);
  };

  // 切换模式
  const handleModeChange = (mode) => {
    state.mode = mode;
  };

  // 选择会话 - 加载历史消息
  const handleSelectConversation = async (id) => {
    state.sessionId = id;
    state.conversationLoading = true;
    try {
      const res = await getAiSessionDetail({ sessionId: id });
      if (res?.success && res?.data) {
        chatAreaRef.current?.loadHistory(res.data);
      } else {
        message.error('加载会话详情失败');
      }
    } catch (err) {
      console.error('加载会话详情失败:', err);
      message.error('加载会话详情失败');
    } finally {
      state.conversationLoading = false;
    }
  };

  // 新建会话
  const handleNewConversation = () => {
    state.sessionId = null;
    chatAreaRef.current?.clearMessages();
    conversationListRef.current?.refresh();
  };

  // 切换资产库
  const toggleAssetLibrary = () => {
    state.assetLibraryVisible = !state.assetLibraryVisible;
  };

  // 切换侧边栏折叠状态
  const toggleSidebarCollapse = () => {
    state.sidebarCollapsed = !state.sidebarCollapsed;
  };

  // 刷新生成项后，刷新左侧列表
  const handleRefreshList = () => {
    conversationListRef.current?.refresh();
  };

  // 发送成功后，更新 sessionId
  const handleSessionIdChange = (id) => {
    state.sessionId = id;
  };

  return (
    <div className="ai-creation-center">
      {/* 左侧：对话列表 */}
      <div
        className={`sidebar${
          state.sidebarCollapsed ? ' sidebarCollapsed' : ''
        }`}
      >
        <ConversationList
          ref={conversationListRef}
          currentId={state.sessionId}
          onSelect={handleSelectConversation}
          onNew={handleNewConversation}
          collapsed={state.sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
        />
      </div>

      {/* 中间：聊天区域 */}
      <div
        className={`main${dragOver ? ' mainDragOver' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* 拖拽遮罩 */}
        {dragOver && (
          <div className="dragOverlay">
            <PlusOutlined className="dragOverlayIcon" />
            <span className="dragOverlayText">松开鼠标上传图片</span>
          </div>
        )}
        <ChatArea
          ref={chatAreaRef}
          mode={state.mode}
          conversationId={state.sessionId}
          conversationLoading={state.conversationLoading}
          onModeChange={handleModeChange}
          onToggleAssetLibrary={toggleAssetLibrary}
          onRefreshList={handleRefreshList}
          onSessionIdChange={handleSessionIdChange}
          sidebarCollapsed={state.sidebarCollapsed}
          onExpandSidebar={toggleSidebarCollapse}
        />
      </div>

      {/* 右侧：资产库 */}
      {state.assetLibraryVisible && (
        <div className="assetPanel">
          <AssetLibrary onClose={toggleAssetLibrary} />
        </div>
      )}
    </div>
  );
};

export default AiCreationCenter;
